import { useCallback, useEffect, useState } from 'react';
import { apiGet } from '../client';

// Module-level cache — survives re-renders and re-mounts within the same session.
let cachedTags: string[] | null = null;
let fetchPromise: Promise<string[]> | null = null;

/** Invalidate the module-level cache (call after a tag is created/deleted). */
export function invalidateTagsCache(): void {
  cachedTags = null;
  fetchPromise = null;
}

async function fetchAllTags(): Promise<string[]> {
  if (fetchPromise) return fetchPromise;

  fetchPromise = apiGet<{ tags: string[] }>('/app/v1/tags').then((res) => {
    cachedTags = res.tags ?? [];
    fetchPromise = null;
    return cachedTags;
  });

  return fetchPromise;
}

/**
 * Returns all tags belonging to the current user, sorted alphabetically.
 * Result is cached at the module level — no duplicate fetches within the session.
 * Call `invalidateTagsCache()` after mutations to force a fresh fetch.
 */
export function useAllTags() {
  const [tags, setTags] = useState<string[]>(cachedTags ?? []);
  const [loading, setLoading] = useState<boolean>(cachedTags === null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (cachedTags !== null) {
      setTags(cachedTags);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAllTags();
      setTags(result);
    } catch (e: any) {
      setError(e?.message || 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { tags, loading, error, reload: load };
}

